import { UcoastEl } from '@/scripts/core/UcoastEl'
import { FEATURED_PRODUCT_VARIANT_CHANGE_EVENT } from '@/scripts/theme/constants'
import { type ProductForm } from '@/scripts/product/product-form'
import type { ProductVariant } from '@/scripts/shopify'
import {
	beginStandardProductSelection,
	resolveStandardProductSelection,
} from '@/scripts/platform/standard-events'
import { motionEnabled } from '@/scripts/core/motion'
import { replaceProductUrlParameters } from '@/scripts/product/product-url'

export class HeroFeaturedProduct extends UcoastEl {
	static htmlSelector = 'hero-featured-product'

	private productForm: HTMLFormElement | null = null
	private ATCButton: HTMLButtonElement | null = null
	private variants: NodeListOf<HTMLElement>
	private variantPannel: HTMLElement | null
	private heroMedia: HTMLElement | null
	private featuredMedia: HTMLElement | null
	private defaultFeaturedMedia: Node[]
	private selectShadeButton: HTMLButtonElement | null = null
	private shadeLabel: HTMLElement | null = null
	private panelRequest?: AbortController
	private requiresShade: boolean
	private hasSelectedShade: boolean
	private variantData: ProductVariant[]

	constructor() {
		super()

		this.productForm = this.querySelector<HTMLFormElement>('form') ?? null
		this.selectShadeButton = this.querySelector<HTMLButtonElement>('.select-shade__button') ?? null
		this.shadeLabel = this.querySelector<HTMLElement>('[data-selected-shade-label]') ?? null
		this.requiresShade = this.hasAttribute('data-requires-shade')
		this.hasSelectedShade = this.hasAttribute('data-has-selected-shade')
		const variantData = this.querySelector<HTMLScriptElement>(
			'[data-uc-variant-data]'
		)?.textContent
		this.variantData = variantData ? JSON.parse(variantData) : []

		this.ATCButton =
			this.productForm?.querySelector<HTMLButtonElement>(
				'.product-form__submit'
			) ?? null

		this.variants = this.querySelectorAll<HTMLElement>(
			'.hero__featured-product__variant'
		)

		const product = this.closest<HTMLElement>('.product')

		this.variantPannel = product?.querySelector<HTMLElement>('.product-media__panels') ?? null
		this.heroMedia = product?.querySelector<HTMLElement>('[data-product-hero-media]') ?? null
		this.featuredMedia = this.querySelector<HTMLElement>('[data-featured-product-media]') ?? null
		this.defaultFeaturedMedia = Array.from(
			this.featuredMedia?.childNodes ?? [],
			(node) => node.cloneNode(true)
		)


		if (!this.productForm || !this.ATCButton) {
			return
		}

		if (this.variants.length > 0 && !this.hasSelectedShade) {
			this.ATCButton.disabled = true
		}

		if (this.requiresShade && !this.hasSelectedShade) {
			this.ATCButton.disabled = false
			this.ATCButton.type = 'button'
			this.ATCButton.addEventListener('click', this.handleShadePrompt)
		}

		this.setupVariantListeners()

		if (this.selectShadeButton) {
			this.ATCButton.disabled = false
			this.selectShadeButton.addEventListener('click', () => {
				this.classList.remove('has-shade')
			})
		}
	}

	override disconnectedCallback(): void {
		this.ATCButton?.removeEventListener('click', this.handleShadePrompt)
		this.panelRequest?.abort()
		super.disconnectedCallback()
	}

	private handleShadePrompt = (event: MouseEvent): void => {
		if (this.hasSelectedShade) return

		event.preventDefault()
		this.closest('product-info')?.dispatchEvent(
			new CustomEvent('product-info:expand')
		)
	}

	private setupVariantListeners(): void {
		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (!input) {
				return
			}

			input.addEventListener('change', () => {
				if (input.checked) {
					this.selectVariant(input)
				}
			})
		})
	}

	private selectVariant(input: HTMLInputElement): void {
		if (!this.productForm || !this.ATCButton) {
			return
		}
		const variantId = input.value

		const variantInput =
			this.productForm.querySelector<HTMLInputElement>(
				'[data-uc-product-variant-id]'
			)

		if (!variantInput) {
			return
		}

		const variant = this.variantData.find(
			(candidate) => `${candidate.id}` === variantId
		)
		const standardSelection = beginStandardProductSelection(
			this,
			variant?.options ?? []
		)

		variantInput.value = variantId

		this.hasSelectedShade = true
		this.setAttribute('data-has-selected-shade', '')
		if (variant?.title && this.shadeLabel) {
			this.shadeLabel.textContent = variant.title
		}
		this.ATCButton.disabled = false
		this.ATCButton.type = 'submit'
		this.updateCommerceState(input)
		this.updateProductUrl(variantId)
		this.renderFeaturedMedia(variantId)

		this.variants.forEach((variant) => {
			const input = variant.querySelector<HTMLInputElement>('input')

			if (input) {
				input.checked = input.value === variantId
				variant.classList.toggle(
					'is-selected',
					input.value === variantId
				)
			}
		})

		this.closest('section')
			?.querySelector<ProductForm>('sticky-atc product-form')
			?.syncStickyFormFields()

		this.closest('.featured-product-carousel')?.dispatchEvent(
			new CustomEvent(FEATURED_PRODUCT_VARIANT_CHANGE_EVENT, {
				detail: { variantId },
			})
		)

		void this.renderVariantPanel(variantId)
		resolveStandardProductSelection(standardSelection, variant)
	}

	private updateProductUrl(variantId: string): void {
		if (!this.dataset.productUrl || this.dataset.updateUrl === 'false') return
		replaceProductUrlParameters(
			{ variant: variantId },
			this.dataset.productUrl
		)
	}

	private renderFeaturedMedia(variantId: string): void {
		if (!this.featuredMedia) return
		const template = Array.from(
			this.querySelectorAll<HTMLTemplateElement>('[data-variant-media-template]')
		).find((candidate) => candidate.dataset.variantMediaTemplate === variantId)
		const media = template
			? Array.from(template.content.childNodes, (node) => node.cloneNode(true))
			: this.defaultFeaturedMedia.map((node) => node.cloneNode(true))

		this.featuredMedia.replaceChildren(...media)
	}

	private updateCommerceState(input: HTMLInputElement): void {
		if (!this.ATCButton) return
		const inventoryStatus = input.dataset.inventoryStatus
		const waitlist =
			inventoryStatus === 'ComingSoon' || inventoryStatus === 'OutOfStock'
		const available = input.dataset.variantAvailable === 'true'
		const inventoryStatusText = this.ATCButton.querySelector<HTMLElement>(
			'[data-uc-inventory-status-text]'
		)

		this.ATCButton.disabled = !available && !waitlist
		this.ATCButton.toggleAttribute('aria-disabled', !available && !waitlist)
		if (waitlist) {
			this.ATCButton.dataset.oosPopupTrigger = input.value
			this.ATCButton.dataset.productTitle = this.dataset.productTitle ?? ''
		} else {
			delete this.ATCButton.dataset.oosPopupTrigger
			delete this.ATCButton.dataset.productTitle
		}
		if (inventoryStatusText && input.dataset.inventoryActionText) {
			inventoryStatusText.textContent = input.dataset.inventoryActionText
		}
	}

	private async renderVariantPanel(variantId: string): Promise<void> {
		this.variantPannel ??=
			this.closest<HTMLElement>('.product')?.querySelector<HTMLElement>(
				'.product-media__panels'
			) ?? null

		if (!this.variantPannel) {
			return
		}

		const sectionId =
			this.closest<HTMLElement>('section')?.dataset.section

		if (!sectionId) {
			return
		}

		this.panelRequest?.abort()
		const panelRequest = new AbortController()
		this.panelRequest = panelRequest

		try {
			const url = new URL(window.location.href)

			url.searchParams.set('section_id', sectionId)
			url.searchParams.set('variant', variantId)

			const response = await fetch(url.toString(), {
				signal: panelRequest.signal,
			})

			if (!response.ok) {
				throw new Error(`Section render failed: ${response.status}`)
			}

			const html = await response.text()


			const parser = new DOMParser()
			const doc = parser.parseFromString(html, 'text/html')

			const newVariantPanel =
				doc.querySelector<HTMLElement>('.product-media__panels')
			const newHeroMedia =
				doc.querySelector<HTMLElement>('[data-product-hero-media]')

			if (!newVariantPanel) {
				return
			}

			if (this.panelRequest !== panelRequest) return

			const subscriptionSource = doc.getElementById(`Subscription-${sectionId}`)
			const subscriptionDestination = document.getElementById(
				`Subscription-${sectionId}`
			)
			if (subscriptionSource && subscriptionDestination) {
				subscriptionDestination.innerHTML = subscriptionSource.innerHTML
				subscriptionDestination.toggleAttribute(
					'hidden',
					subscriptionSource.hasAttribute('hidden')
				)
			}

			const heroChanged = Boolean(
				newHeroMedia &&
				this.heroMedia &&
				newHeroMedia.dataset.productHeroPanelId !==
					this.heroMedia.dataset.productHeroPanelId
			)
			const outgoing = [
				this.variantPannel,
				...(heroChanged && this.heroMedia ? [this.heroMedia] : []),
			]
			await this.fadeMedia(outgoing, 1, 0, 120)
			if (this.panelRequest !== panelRequest) return

			const incoming: HTMLElement[] = [newVariantPanel]
			if (heroChanged && newHeroMedia && this.heroMedia) {
				this.heroMedia.replaceWith(newHeroMedia)
				this.heroMedia = newHeroMedia
				incoming.push(newHeroMedia)
			}

			this.variantPannel.replaceWith(newVariantPanel)
			this.variantPannel = newVariantPanel
			void this.fadeMedia(incoming, 0, 1, 220)
		} catch (error) {
			if (error instanceof DOMException && error.name === 'AbortError') return
			console.error('Failed to render variant panel:', error)
		} finally {
			if (this.panelRequest === panelRequest) {
				this.panelRequest = undefined
			}
		}
	}

	private async fadeMedia(
		elements: HTMLElement[],
		from: number,
		to: number,
		duration: number
	): Promise<void> {
		if (!motionEnabled()) return
		const animations = elements.map((element) =>
			element.animate([{ opacity: from }, { opacity: to }], {
				duration,
				easing: 'ease',
			})
		)
		await Promise.all(
			animations.map((animation) => animation.finished.catch(() => undefined))
		)
	}
}
